import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from 'src/environments/environment';
import {
  ProductDataFilenameConvention,
  ProductDataFilenameConventionCreatePayload,
} from '../../models/product-data-filename-conventions/product-data-filename-convention';

interface LatestResponse {
  productDataFilenameConvention: ProductDataFilenameConvention | null;
}

interface CreateResponse {
  productDataFilenameConvention: ProductDataFilenameConvention;
}

interface ListResponse {
  productDataFilenameConventions: ProductDataFilenameConvention[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductDataFilenameConventionService {
  constructor(private http: HttpClient) {}

  getLatest(organizationId: string): Observable<ProductDataFilenameConvention | null> {
    const url = `${environment.apiBase}/organizations/${organizationId}/product-data-filename-conventions/latest`;
    if (isDevMode()) {
      console.log('[DEV] [ProductDataFilenameConventionService] loading latest', organizationId);
    }
    return this.http.get<LatestResponse>(url).pipe(
      map((res: LatestResponse) => res.productDataFilenameConvention ?? null),
      // Backend returns 404 when the org itself doesn't exist (design divergence
      // from GetLatestDataConvention). Treat that as "no convention" for the
      // card's purposes; the OrganizationUpdate host handles missing-org
      // navigation separately via the org-load path.
      catchError((err) => {
        if (err?.status === 404) return of(null);
        throw err;
      }),
    );
  }

  create(
    organizationId: string,
    payload: ProductDataFilenameConventionCreatePayload,
  ): Observable<ProductDataFilenameConvention> {
    const url = `${environment.apiBase}/organizations/${organizationId}/product-data-filename-conventions`;
    if (isDevMode()) {
      console.log('[DEV] [ProductDataFilenameConventionService] creating', organizationId, payload);
    }
    return this.http
      .post<CreateResponse>(url, payload)
      .pipe(map((res: CreateResponse) => res.productDataFilenameConvention));
  }

  list(organizationId: string): Observable<ProductDataFilenameConvention[]> {
    const url = `${environment.apiBase}/organizations/${organizationId}/product-data-filename-conventions`;
    if (isDevMode()) {
      console.log('[DEV] [ProductDataFilenameConventionService] listing', organizationId);
    }
    return this.http
      .get<ListResponse>(url)
      .pipe(map((res: ListResponse) => res.productDataFilenameConventions ?? []));
  }
}
